import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  BarChart3,
  CalendarClock,
  LogOut,
  Menu,
  Moon,
  Package,
  Receipt,
  ShoppingCart,
  Sun,
  UserCircle,
  Wallet,
  X,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useAuth } from '@/auth/AuthContext';

/** Los siete módulos del sistema, en el orden en que se usan en el mostrador. */
export const NAV_ITEMS = [
  { path: '/', label: 'Reporte', icon: BarChart3 },
  { path: '/ventas', label: 'Ventas', icon: ShoppingCart },
  { path: '/stock', label: 'Stock', icon: Package },
  { path: '/caja', label: 'Caja', icon: Wallet },
  { path: '/gastos', label: 'Gastos', icon: Receipt },
  { path: '/clientes', label: 'Clientes', icon: UserCircle },
  { path: '/agenda', label: 'Agenda', icon: CalendarClock },
];

const THEME_KEY = '7en1.theme';

function useDarkMode() {
  const [dark, setDark] = useState(() => localStorage.getItem(THEME_KEY) === 'dark');

  useEffect(() => {
    document.documentElement.classList.toggle('dark', dark);
    localStorage.setItem(THEME_KEY, dark ? 'dark' : 'light');
  }, [dark]);

  return [dark, () => setDark((d) => !d)];
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const [dark, toggleDark] = useDarkMode();
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  const initial = user?.name?.charAt(0)?.toUpperCase() || 'A';

  const navClasses = (isActive, extra = '') =>
    `${extra} transition-colors duration-200 ${
      isActive
        ? 'bg-primary text-primary-foreground'
        : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
    }`;

  return (
    <div className="flex min-h-screen flex-col bg-background">
      {/* Cabecera móvil */}
      <header className="sticky top-0 z-50 border-b border-border bg-background lg:hidden">
        <div className="flex h-14 items-center justify-between px-4">
          <div className="flex items-center gap-2">
            <img src="/app-icon-v2.jpeg" alt="" className="h-8 w-8 rounded-sm object-cover" />
            <span className="text-lg font-black tracking-tighter text-primary">7 en 1</span>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" onClick={toggleDark} aria-label="Cambiar tema">
              {dark ? <Sun size={18} /> : <Moon size={18} />}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMenuOpen((open) => !open)}
              aria-label="Menú"
            >
              {menuOpen ? <X size={20} /> : <Menu size={20} />}
            </Button>
          </div>
        </div>

        {menuOpen && (
          <div className="border-t border-border bg-card p-4">
            <div className="flex items-center gap-3 rounded-sm bg-muted p-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary font-bold text-primary-foreground">
                {initial}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{user?.name}</p>
                <p className="truncate text-xs text-muted-foreground">{user?.email}</p>
              </div>
              <Button variant="ghost" size="icon" onClick={logout} aria-label="Cerrar sesión">
                <LogOut size={18} />
              </Button>
            </div>
          </div>
        )}
      </header>

      <div className="flex flex-1">
        {/* Barra lateral de escritorio */}
        <aside className="hidden w-64 shrink-0 border-r border-border bg-card lg:block">
          <div className="sticky top-0 flex h-screen flex-col">
            <div className="border-b border-border bg-background p-6">
              <div className="flex items-center gap-3">
                <img src="/app-icon.jpeg" alt="" className="h-10 w-10 rounded-sm object-cover" />
                <span className="text-2xl font-black tracking-tighter text-primary">7 en 1</span>
              </div>
            </div>

            <nav className="flex-1 space-y-1 overflow-y-auto p-4">
              {NAV_ITEMS.map(({ path, label, icon: Icon }) => (
                <Link
                  key={path}
                  to={path}
                  className={navClasses(
                    location.pathname === path,
                    'flex items-center gap-3 rounded-sm px-4 py-3 text-sm font-medium'
                  )}
                >
                  <Icon size={18} />
                  {label}
                </Link>
              ))}
            </nav>

            <div className="border-t border-border p-4">
              <div className="flex items-center justify-between gap-2">
                <div className="flex min-w-0 items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary font-bold text-primary-foreground">
                    {initial}
                  </div>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{user?.name}</p>
                    <p className="truncate text-xs capitalize text-muted-foreground">{user?.role}</p>
                  </div>
                </div>
                <Button variant="ghost" size="icon" onClick={logout} aria-label="Cerrar sesión">
                  <LogOut size={18} />
                </Button>
              </div>
            </div>
          </div>
        </aside>

        <div className="flex min-w-0 flex-1 flex-col">
          <header className="hidden border-b border-border bg-background lg:block">
            <div className="flex h-16 items-center justify-end px-8">
              <Button
                variant="outline"
                size="icon"
                onClick={toggleDark}
                className="rounded-sm"
                aria-label="Cambiar tema"
              >
                {dark ? <Sun size={18} /> : <Moon size={18} />}
              </Button>
            </div>
          </header>

          <main className="flex-1 overflow-x-hidden p-4 pb-24 lg:p-8 lg:pb-8">{children}</main>
        </div>
      </div>

      {/* Navegación inferior móvil */}
      <nav className="fixed bottom-0 left-0 right-0 z-50 border-t border-border bg-background lg:hidden">
        <div className="flex gap-1 overflow-x-auto p-2">
          {NAV_ITEMS.map(({ path, label, icon: Icon }) => (
            <Link
              key={path}
              to={path}
              onClick={() => setMenuOpen(false)}
              className={navClasses(
                location.pathname === path,
                'flex min-w-[58px] flex-1 flex-col items-center gap-1 rounded-sm p-1.5'
              )}
            >
              <Icon size={18} />
              <span className="text-[9px] font-medium">{label}</span>
            </Link>
          ))}
        </div>
      </nav>
    </div>
  );
}
